import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning } from '@lightningjs/sdk'

import { Registry } from '@lightningjs/sdk'
import {
  getColor,
  grey300,
  grey50,
  grey700,
  grey960,
  transparent,
  white,
} from '../../constants/colors'
import { UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { createInput } from '../../utils/misc'
import { calculateRenderInfo } from '../../utils/text'
import SpeechComp from '../speechComp/speechComp'
import { HIDDEN } from '../TvCards/templates'

const OVERFLOW_PADDING = 10
const CURSOR_BLINK_DELAY = 750
const WINDOW_OFFSET = 10
const PRECISION_OVERRIDE = 1

export class NativeInputBox extends SpeechComp {
  private _inputCreated = false
  private _isTransparent = false
  private _isPassword = false
  private _text: string = ''
  private _placeholder: string
  private _textColor: string
  private _placeholderColor: string
  private _fontFace: string
  private _fontSize: number
  private _onCompleteSignal: string
  private _onTextChangeSignal: string
  private _closeInput: Function
  private _cursorIntervalId: number
  private _cursorPosition: number = 0

  /**
   * Templates native input box
   * @returns
   */
  static _template() {
    return {
      collision: true,

      InputContentWrapper: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        clipping: true,
        flex: {
          alignItems: 'center',
        },
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
        },
        NativeInputBox: {
          flex: {
            alignItems: 'center',
            direction: 'row',
          },
          IconWrapper: {
            flex: {
              alignItems: 'center',
              direction: 'row',
              paddingLeft: 34,
              paddingRight: 24,
              paddingTop: 2,
              paddingBottom: 2,
            },
            rect: true,
            color: getColor(grey960),
            zIndex: 1,
            Icon: {
              w: 32,
              h: 32,
              color: getColor(white),
            },
          },
          shader: null,
          Cursor: {
            h: 35,
            w: 2,
            rect: true,
            color: getColor(grey300),
            alpha: HIDDEN,
          },
          Text: {
            flexItem: {
              marginTop: 5,
            },
            text: {
              text: '',
              fontSize: 22,
              fontFace: UniNeueRegular,
              textColor: getColor(white),
              lineHeight: 30,
              borderColor: null,
            },
          },
        },
      },
      OuterBorder: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: transparent,
        zIndex: 2,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          stroke: 2,
          strokeColor: getColor(grey700),
          transparent: true,
          radius: BORDER_RADIUS,
        },
      },
    }
  }

  get inputContentWrapper() {
    return this.tag('InputContentWrapper')
  }

  get outerBorder() {
    return this.tag('OuterBorder')
  }

  get nativeInputBox() {
    return this.tag('NativeInputBox')
  }

  get cursor() {
    return this.tag('Cursor')
  }

  get contentText() {
    return this.tag('Text')
  }

  set isTransparent(v) {
    this._isTransparent = v
  }

  get iconWrapper() {
    return this.tag('IconWrapper')
  }

  get icon() {
    return this.tag('Icon')
  }

  set isPassword(v: boolean) {
    this._isPassword = v
    this._updateTextAndColor()
  }

  get isPassword() {
    return this._isPassword
  }

  set iconImage(v: string) {
    this.icon.patch({
      texture: v,
    })
  }

  get fontFace() {
    return this._fontFace
  }

  set fontFace(v: string) {
    this._fontFace = v
  }

  get fontSize() {
    return this._fontSize
  }

  set fontSize(v: number) {
    this._fontSize = v
  }

  get placeholder() {
    return this._placeholder
  }

  set placeholder(v: string) {
    this._placeholder = v
  }

  get placeholderColor() {
    return this._placeholderColor
  }

  set placeholderColor(v: string) {
    this._placeholderColor = v
  }

  get textColor() {
    return this._textColor
  }

  set textColor(v: string) {
    this._textColor = v
  }

  get onCompleteSignal() {
    return this._onCompleteSignal
  }

  set onCompleteSignal(v: string) {
    this._onCompleteSignal = v ?? 'onComplete'
  }

  get onTextChangeSignal() {
    return this._onTextChangeSignal
  }

  set onTextChangeSignal(v: string) {
    this._onTextChangeSignal = v ?? 'onTextChange'
  }

  set inColor(v: string) {
    this.icon.patch({
      color: getColor(v),
    })
  }

  get inputCreated() {
    return this._inputCreated
  }

  get visibleAreaLength() {
    return this.w - this.iconWrapper.finalW
  }

  get isInputTextOverflowing() {
    return this.contentText.finalW > this.visibleAreaLength
  }

  _init() {
    const { color, placeholder, placeholderColor, fontSize, fontFace } = <NativeInputBox>this
    this.inputContentWrapper.patch({
      color: color ?? getColor(grey960),
    })
    this.nativeInputBox.patch({
      Text: {
        text: {
          text: placeholder ?? 'Enter text...',
          fontSize: fontSize ?? 20,
          textColor: placeholderColor ?? getColor(white),
          fontFace: fontFace,
        },
      },
    })
  }

  _disable() {
    Registry.clearInterval(this._cursorIntervalId)
  }

  _focus() {
    this.focusInputBox(true)
    this.speak()
  }

  _unfocus() {
    this._closeInput?.()
    this.focusInputBox(false)
    this.hideCursor()
  }

  focusInputBox(isFocused: boolean) {
    this.outerBorder.patch({
      shader: { strokeColor: isFocused ? getColor(grey50) : this.getUnfocusColor() },
    })
  }

  getUnfocusColor() {
    return this._isTransparent ? transparent : getColor(grey700)
  }

  getText() {
    return this._text
  }

  setText(v: string) {
    this._text = v
    this._updateTextAndColor()
  }

  _handleBack() {
    // In webOS, pressing BACK while they keyboard is showing will NOT trigger this event,
    // nor the onComplete event handler passed to the createInput() method.
    return isWebos ? false : this._inputCreated
  }

  _handleEnter() {
    if (isVizio) {
      this.fireAncestors('$focusVizioKeyboard')
      return
    }

    if (this._inputCreated) return

    this._inputCreated = true
    this._closeInput = createInput({
      text: this._text ?? '',
      type: 'email',
      onComplete: (value) => {
        this._closeInput = null
        this._inputCreated = false
        this._text = value || null
        this._updateTextAndColor()
        this.hideCursor()
        this.signal(this._onCompleteSignal, value)
      },
      onInput: (value) => {
        const wordLengthBeforeUpdate = this._text?.length ?? 0
        const wordlengthAfterUpdate = value?.length ?? 0
        this._text = value || null
        this._updateTextAndColor()

        // Needs to be done inside a requestAnimationFrame as calling this.stage.update() doesn't
        // refresh to the latest finalW after _updateTextAndColor()
        requestAnimationFrame(() => {
          if (this.isInputTextOverflowing) {
            const lengthDifference = this.contentText.finalW - this.visibleAreaLength
            this.contentText.x = -lengthDifference - OVERFLOW_PADDING
            this.updateCursorPositionWithOverflow(wordlengthAfterUpdate > wordLengthBeforeUpdate)
          } else {
            this.contentText.x = 0

            if (this.contentText?.text?.text === this._placeholder) {
              this.cursor.x = 0
              this._cursorPosition = 0
            } else if (this._cursorPosition === wordLengthBeforeUpdate) {
              this.cursor.x = this.contentText.finalW
              this._cursorPosition = wordlengthAfterUpdate
            } else {
              this.updateCursorPosition(wordlengthAfterUpdate > wordLengthBeforeUpdate)
            }
          }
        })

        this.signal(this._onTextChangeSignal, value)
      },
    })
    this.showCursor()
  }

  _handleLeft() {
    if (this._cursorPosition > 0 && this.isInputTextOverflowing) {
      this.updateCursorPositionWithOverflow(false)
    } else if (this._cursorPosition > 0) {
      this.updateCursorPosition(false)
    }
  }

  _handleRight() {
    if (this._cursorPosition < this._text.length && this.isInputTextOverflowing) {
      this.updateCursorPositionWithOverflow(true)
    } else if (this._cursorPosition < this._text.length) {
      this.updateCursorPosition(true)
    }
  }

  updateCursorPosition(increasePosition = true) {
    if (increasePosition) {
      this._cursorPosition += 1
    } else {
      this._cursorPosition -= 1
    }

    const { wordWidth } = calculateRenderInfo(
      this.contentText.text,
      this.contentText?.text?.text?.substring(0, this._cursorPosition),
      PRECISION_OVERRIDE,
    )
    this.cursor.x = wordWidth
  }

  updateCursorPositionWithOverflow(increasePosition = true) {
    if (increasePosition) {
      this._cursorPosition += 1
    } else {
      this._cursorPosition -= 1
    }

    const lengthDifference = this.contentText.finalW - this.visibleAreaLength
    const contentOffset = lengthDifference + OVERFLOW_PADDING
    const { wordWidth } = calculateRenderInfo(
      this.contentText.text,
      this.contentText?.text?.text?.substring(0, this._cursorPosition),
      PRECISION_OVERRIDE,
    )

    const leftWindowPosition = this._cursorPosition >= 0 ? this._cursorPosition - WINDOW_OFFSET : 0
    const { wordWidth: leftWordWidth } = calculateRenderInfo(
      this.contentText.text,
      this.contentText?.text?.text?.substring(0, leftWindowPosition),
      PRECISION_OVERRIDE,
    )

    const cursorDelta = wordWidth - contentOffset
    this.cursor.x = cursorDelta < 0 ? 0 : cursorDelta
    // The w is the longest character. If the proximity of the cursor to the left edge is less than the width
    // of a w, shift the whole text so we ensure that the next character to the left of the cursor will be completely visible
    const { wordWidth: leftEdgeTreshold } = calculateRenderInfo(
      this.contentText.text,
      'w',
      PRECISION_OVERRIDE,
    )

    if (
      cursorDelta < leftEdgeTreshold ||
      (cursorDelta >= leftEdgeTreshold &&
        this.contentText.x > -contentOffset &&
        leftWordWidth < contentOffset)
    ) {
      this.contentText.x = -leftWordWidth
      this.cursor.x = Math.abs(wordWidth - leftWordWidth)
    } else {
      this.contentText.x = -contentOffset
    }
  }

  hideCursor() {
    Registry.clearInterval(this._cursorIntervalId)
    this._cursorIntervalId = null
    this._cursorPosition = this.contentText?.text?.text?.length ?? 0
    this.cursor.x = this._cursorPosition === 0 ? 0 : this.contentText.finalW
    this.cursor.alpha = HIDDEN
  }

  showCursor() {
    this.cursor.alpha = 1
    Registry.clearInterval(this._cursorIntervalId)
    this._cursorIntervalId = Registry.setInterval(() => {
      this.cursor.alpha = this.cursor.alpha === HIDDEN ? 1 : HIDDEN
    }, CURSOR_BLINK_DELAY)

    if (this.contentText?.text?.text === this._placeholder) {
      this.cursor.x = 0
      this._cursorPosition = 0
    } else if (this.isInputTextOverflowing) {
      const lengthDifference = this.contentText.finalW - this.visibleAreaLength
      const contentOffset = lengthDifference + OVERFLOW_PADDING
      const { wordWidth } = calculateRenderInfo(this.contentText.text, null, PRECISION_OVERRIDE)

      this.contentText.x = -contentOffset
      this._cursorPosition = this.contentText?.text?.text?.length
      this.cursor.x = wordWidth - contentOffset
    }
  }

  resetCursor() {
    this._cursorPosition = this.contentText?.text?.text?.length ?? 0
    this.cursor.x = this._cursorPosition === 0 ? 0 : this.contentText.finalW
  }

  toggleIsPassword() {
    this._isPassword = !this._isPassword
    this._updateTextAndColor()

    // Using this.stage.update() doesn't reflect the toggled password string, hence we need to wait for the next render
    requestAnimationFrame(() => {
      this._cursorPosition = this.contentText?.text?.text?.length ?? 0
      this.cursor.x = this._cursorPosition === 0 ? 0 : this.contentText.finalW
    })
  }

  startInput() {
    this._handleEnter()
  }

  closeInput() {
    this._closeInput?.()
  }

  _updateTextAndColor() {
    let text = ''
    if (this._isPassword) {
      text = this._text ? this._text.replace(/./g, '*') : this._placeholder
    } else {
      text = this._text ? this._text : this._placeholder
    }

    this.contentText.patch({
      text: {
        text: text ?? this._placeholder,
        fontSize: this._fontSize ?? 20,
        fontFace: this._fontFace,
        textColor: this._text ? this._textColor : this._placeholderColor,
      },
    })
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this.focusInputBox(false)
  }
}
